import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { setLoading } from "../../reducer/auth/auth";
import "./style.css";

// Client-side display metadata for each CONFIGURABLE session type.
// group_coaching was hidden here for a while (2026-08-19 product decision,
// forced to 100% instead of medium-configurable) but that hide was
// explicitly reversible and has since been undone (2026-08-24, "2nd part of
// this feature") — it's a normal row again, same as mediumship/coaching.
const TYPE_META = {
  mediumship: {
    title: "1:1 Sessions",
    sub: "Private readings",
    unit: (n) => (n === 1 ? "appointment" : "appointments"),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#500ADE" strokeWidth="1.8">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c0-4 3.5-7 8-7s8 3 8 7" />
      </svg>
    ),
  },
  coaching: {
    title: "Coaching",
    sub: "1:1 coaching sessions",
    unit: (n) => (n === 1 ? "appointment" : "appointments"),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#500ADE" strokeWidth="1.8">
        <path d="M12 3c-4 3-4 8-4 8s-3-1-5 1c2 4 6 4 6 4M12 3c4 3 4 8 4 8s3-1 5 1c-2 4-6 4-6 4M12 11v10" />
      </svg>
    ),
  },
  group_coaching: {
    title: "Group Coaching",
    sub: "Group coaching sessions",
    unit: (n) => (n === 1 ? "session" : "sessions"),
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#500ADE" strokeWidth="1.8">
        <circle cx="8" cy="9" r="3" />
        <circle cx="16" cy="9" r="3" />
        <path d="M2 20c0-3.5 2.7-6 6-6s6 2.5 6 6M10 20c0-3.5 2.7-6 6-6s6 2.5 6 6" />
      </svg>
    ),
  },
};

// All three configurable types default to the same starting point — nth=20
// (pct(20) = 5%) — and start out disabled (see the "never saved before"
// branch of normalizeRules() below). A medium must explicitly flip a type's
// "Include" switch on before it starts counting toward anything; once they
// do, this is the interval it starts at, adjustable from there.
const DEFAULT_NTH = { mediumship: 20, coaching: 20, group_coaching: 20 };

// Fallback shape for "Your Impact" — used only if data.impact is ever
// missing (e.g. an older cached response or a request error). As of Phase 3,
// the settings service always returns this same shape with real numbers
// derived from paid Transaction rows, so in normal operation this
// placeholder is never actually shown.
const IMPACT_PLACEHOLDER = {
  total_donated: 0,
  currency: "USD",
  payments_shared_count: 0,
  supporting_since: null,
};

// The three appointment types this page always shows, in display order.
// The backend only ever reports rows a medium has actually saved before —
// any type missing from the API response is rendered here using these
// client-side defaults instead, so the form is always fully populated and
// ready to save as-is.
const CANONICAL_TYPE_KEYS = Object.keys(TYPE_META);

const pct = (n) => (n > 0 ? Math.round(100 / n) : 0);

const RING_SIZE = 58;
const RING_CENTER = RING_SIZE / 2;
const RING_R = 25;
const RING_CIRC = 2 * Math.PI * RING_R;

function CycleIndicator({ nth, count }) {
  const n = parseInt(nth, 10) || 0;
  if (n <= 1) {
    return (
      <div className="pif-check-circle">
        <svg viewBox="0 0 24 24" fill="none" stroke="#500ADE" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 12l5 5L20 6" />
        </svg>
      </div>
    );
  }
  // posInCycle = how many bookings have landed in the CURRENT cycle so far
  // (0 right when a cycle starts fresh — including a type that's never been
  // booked at all, count=0). Previously this displayed `n` (a full ring)
  // whenever posInCycle was 0, which made an untouched type (count=0) look
  // visually identical to one that had just completed a full cycle
  // (count=n, 2n, ...) — e.g. group coaching showing "10 of 10" with zero
  // real bookings. Show the real in-cycle count instead.
  const posInCycle = count % n;
  const fraction = posInCycle / n;
  return (
    <div className="pif-ring-wrap">
      <svg width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}>
        <circle className="pif-ring-track" cx={RING_CENTER} cy={RING_CENTER} r={RING_R} />
        <circle
          className="pif-ring-progress"
          cx={RING_CENTER}
          cy={RING_CENTER}
          r={RING_R}
          strokeDasharray={RING_CIRC.toFixed(1)}
          strokeDashoffset={(RING_CIRC * (1 - fraction)).toFixed(1)}
        />
      </svg>
      <div className="pif-ring-center">
        <div className="pif-ring-num">{posInCycle}</div>
        <div className="pif-ring-of">of {n}</div>
      </div>
    </div>
  );
}

/**
 * Shared "Share it Forward" settings UI — reused by both the medium's own
 * self-service page (src/pages/medium/payitforward/index.js) and the admin
 * page that manages a medium's settings on their behalf
 * (src/pages/admin/mediumShareItForward/index.js). Neither wrapper knows
 * anything about the other; this component doesn't know whether it's being
 * driven by a medium or an admin either — it just calls whichever API
 * functions it was handed.
 *
 * @param {Object} props
 * @param {() => Promise} props.fetchSettingsApi
 * @param {() => Promise} props.enableApi
 * @param {() => Promise} props.disableApi
 * @param {(payload: Object) => Promise} props.saveRulesApi
 * @param {string} [props.crumbLabel] - breadcrumb root text before "Share it
 *   Forward" (e.g. "Medium Dashboard" for the medium's own page, or a
 *   medium's name for the admin page).
 */
export default function ShareItForwardSettings({
  fetchSettingsApi,
  enableApi,
  disableApi,
  saveRulesApi,
  crumbLabel = "Medium Dashboard",
}) {
  const dispatch = useDispatch();

  const [loaded, setLoaded] = useState(false);
  const [masterOn, setMasterOn] = useState(false);
  const [enabledAt, setEnabledAt] = useState(null);
  const [rules, setRules] = useState([]);
  const [impact, setImpact] = useState(null);
  // Which of CANONICAL_TYPE_KEYS this medium is admin-approved to offer at
  // all (see docs/plans/share-it-forward-service-eligibility-gating.md in
  // the backend repo). A type absent here never gets a row, regardless of
  // whether a saved rule already exists for it.
  const [eligibleTypes, setEligibleTypes] = useState(CANONICAL_TYPE_KEYS);
  // False once we know this medium has no personal Stripe/PayPal/Square keys
  // configured (see PayForwardEligibilityService::hasPersonalPaymentIntegration
  // on the backend) — such a medium is always locked to 100% sharing, live,
  // regardless of any saved rule. Defaults to true (unlocked) until the
  // first fetch resolves, so nothing flashes as locked before we know.
  const [hasPaymentIntegration, setHasPaymentIntegration] = useState(true);
  // Who last touched these settings — the medium themselves, or an admin
  // acting on their behalf. Shown on both the medium's own page and the
  // admin page, so either side can see who made the last change.
  const [lastChangedBy, setLastChangedBy] = useState(null);

  const [pendingConfirm, setPendingConfirm] = useState(null);
  const confirmedKeysRef = useRef(new Set());

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizeRules = (apiRules, eligibleKeys, locked) => {
    const byKey = {};
    (apiRules || []).forEach((r) => {
      byKey[r.session_type_key] = r;
    });

    // Only build a row for a type this medium is actually admin-approved
    // for — a saved rule row can still exist in the database for a type the
    // medium has since lost eligibility for, but it should never render.
    const keysToShow = (eligibleKeys || CANONICAL_TYPE_KEYS).filter((key) =>
      CANONICAL_TYPE_KEYS.includes(key)
    );

    return keysToShow.map((key) => {
      const r = byKey[key];
      if (!r) {
        // Never saved before — show the default, but OFF. A medium has to
        // explicitly turn a type on before it does anything; only once
        // they do does its interval (already pre-filled here) start
        // counting toward anything.
        //
        // Unless this medium has no personal payment gateway at all — then
        // there's nothing to "turn on": every type is already forced to
        // 100% live on the backend (see PayForwardEligibilityService), so
        // reflect that here too instead of showing a misleading OFF default.
        const defaultNth = DEFAULT_NTH[key] || 1;
        return locked
          ? {
              session_type_key: key,
              enabled: true,
              nth: 1,
              savedNth: 1,
              appointment_count: 0,
              last_changed_at: null,
              change_count_90d: 0,
            }
          : {
              session_type_key: key,
              enabled: false,
              nth: defaultNth,
              savedNth: defaultNth,
              appointment_count: 0,
              last_changed_at: null,
              change_count_90d: 0,
            };
      }
      return {
        session_type_key: key,
        enabled: locked ? true : !!r.enabled,
        nth: locked ? 1 : r.nth || DEFAULT_NTH[key] || 1,
        savedNth: locked ? 1 : r.nth || DEFAULT_NTH[key] || 1,
        appointment_count: r.appointment_count || 0,
        last_changed_at: r.last_changed_at || null,
        change_count_90d: r.change_count_90d || 0,
      };
    });
  };

  const fetchSettings = () => {
    dispatch(setLoading(true));
    fetchSettingsApi()
      .then((res) => {
        if (res && res.data && res.data.status) {
          const data = res.data.data || {};
          const eligible = data.eligible_session_types || CANONICAL_TYPE_KEYS;
          const locked = data.has_payment_integration === false;
          setMasterOn(!!data.pay_forward);
          setEnabledAt(data.pay_forward_enabled_at || null);
          setEligibleTypes(eligible);
          setHasPaymentIntegration(!locked);
          setRules(normalizeRules(data.rules, eligible, locked));
          setImpact(data.impact || null);
          setLastChangedBy(data.last_changed_by || null);
        } else {
          // Backend endpoint not available yet / medium has no settings — show the off state.
          setMasterOn(false);
          setRules([]);
        }
        setLoaded(true);
        dispatch(setLoading(false));
      })
      .catch((err) => {
        console.log(err);
        setMasterOn(false);
        setRules([]);
        setLoaded(true);
        dispatch(setLoading(false));
      });
  };

  const handleMasterToggle = () => {
    dispatch(setLoading(true));
    const action = masterOn ? disableApi : enableApi;
    action()
      .then((res) => {
        if (res && res.data && res.data.status) {
          const data = res.data.data || {};
          setMasterOn(!masterOn);
          setLastChangedBy(data.last_changed_by || null);
          if (!masterOn) {
            // just turned on — pick up seeded defaults + enabled_at from the response
            const eligible = data.eligible_session_types || eligibleTypes;
            const locked = data.has_payment_integration === false;
            setEnabledAt(data.pay_forward_enabled_at || enabledAt);
            setEligibleTypes(eligible);
            setHasPaymentIntegration(!locked);
            setRules(normalizeRules(data.rules && data.rules.length ? data.rules : rules, eligible, locked));
            toast.success("Share it Forward turned on — default sharing rules applied.", {
              position: "top-center",
            });
          } else {
            toast.success("Share it Forward turned off.", { position: "top-center" });
          }
        } else {
          toast.error("Couldn't update Share it Forward. Please try again.", {
            position: "top-center",
          });
        }
        dispatch(setLoading(false));
      })
      .catch((err) => {
        console.log(err);
        toast.error("Couldn't update Share it Forward. Please try again.", {
          position: "top-center",
        });
        dispatch(setLoading(false));
      });
  };

  const updateRule = (key, patch) => {
    setRules((prev) =>
      prev.map((r) => (r.session_type_key === key ? { ...r, ...patch } : r))
    );
  };

  const handleNthInput = (key, value) => {
    if (!hasPaymentIntegration) return;
    const clean = value.replace(/[^0-9]/g, "");
    updateRule(key, { nth: clean === "" ? "" : parseInt(clean, 10) });
  };

  const handleStep = (key, delta) => {
    if (!hasPaymentIntegration) return;
    setRules((prev) =>
      prev.map((r) => {
        if (r.session_type_key !== key) return r;
        const current = parseInt(r.nth, 10) || 0;
        const next = Math.max(1, current + delta);
        return { ...r, nth: next };
      })
    );
  };

  const handleRowEnabledToggle = (key) => {
    if (!hasPaymentIntegration) return;
    const row = rules.find((r) => r.session_type_key === key);
    updateRule(key, { enabled: !row.enabled });
  };

  // One-click bulk action: turn every session type on and share every single
  // payment (nth=1 / 100%). This is an explicit, unambiguous choice, so it
  // saves immediately and skips the per-row "you're lowering the interval"
  // confirmation modal that guards against accidental edits.
  const handleShareAllFull = () => {
    // Already forced to 100% for every row while locked (no gateway
    // configured) — nothing to do, and the button is hidden in that state
    // anyway (see render below).
    if (!hasPaymentIntegration) return;
    const updated = rules.map((r) => ({ ...r, nth: 1, enabled: true }));
    setRules(updated);
    updated.forEach((r) => confirmedKeysRef.current.add(r.session_type_key));

    dispatch(setLoading(true));
    const payload = {
      rules: updated.map((r) => ({
        session_type_key: r.session_type_key,
        enabled: true,
        nth: 1,
        confirmed: true,
      })),
    };
    saveRulesApi(payload)
      .then((res) => {
        if (res && res.data && res.data.status) {
          confirmedKeysRef.current = new Set();
          setRules((prev) =>
            prev.map((r) => ({
              ...r,
              nth: 1,
              enabled: true,
              savedNth: 1,
              last_changed_at: "just now",
            }))
          );
          setLastChangedBy((res.data.data && res.data.data.last_changed_by) || null);
          toast.success("Share it Forward is now sharing every payment across all session types.", {
            position: "top-center",
          });
        } else {
          toast.error("Couldn't save your changes. Please try again.", {
            position: "top-center",
          });
        }
        dispatch(setLoading(false));
      })
      .catch((err) => {
        console.log(err);
        toast.error("Couldn't save your changes. Please try again.", {
          position: "top-center",
        });
        dispatch(setLoading(false));
      });
  };

  const findNextOffender = () =>
    rules.find((r) => {
      const n = parseInt(r.nth, 10) || 0;
      return (
        r.enabled &&
        n > 0 &&
        n < r.savedNth &&
        !confirmedKeysRef.current.has(r.session_type_key)
      );
    });

  const handleSaveClick = () => {
    // Nothing to save while locked — every row is already forced to 100%
    // server-side and the button is hidden in that state (see render below).
    if (!hasPaymentIntegration) return;
    const offender = findNextOffender();
    if (offender) {
      setPendingConfirm({
        key: offender.session_type_key,
        label: (TYPE_META[offender.session_type_key] && TYPE_META[offender.session_type_key].title) || offender.session_type_key,
        oldNth: offender.savedNth,
        newNth: parseInt(offender.nth, 10),
      });
      window.loadModal2("#payItForwardConfirmModal", "show");
      return;
    }
    commitSave();
  };

  const handleConfirmCancel = () => {
    if (pendingConfirm) {
      updateRule(pendingConfirm.key, { nth: pendingConfirm.oldNth });
    }
    setPendingConfirm(null);
    window.loadModal2("#payItForwardConfirmModal", "hide");
  };

  const handleConfirmProceed = () => {
    if (pendingConfirm) {
      confirmedKeysRef.current.add(pendingConfirm.key);
    }
    setPendingConfirm(null);
    window.loadModal2("#payItForwardConfirmModal", "hide");
    // re-check in case more than one row was lowered at once
    const nextOffender = findNextOffender();
    if (nextOffender) {
      setTimeout(handleSaveClick, 200);
    } else {
      commitSave();
    }
  };

  const commitSave = () => {
    const payload = {
      rules: rules.map((r) => ({
        session_type_key: r.session_type_key,
        enabled: r.enabled,
        nth: parseInt(r.nth, 10) || r.savedNth,
        confirmed: confirmedKeysRef.current.has(r.session_type_key),
      })),
    };
    dispatch(setLoading(true));
    saveRulesApi(payload)
      .then((res) => {
        if (res && res.data && res.data.status) {
          confirmedKeysRef.current = new Set();
          setRules((prev) =>
            prev.map((r) => ({
              ...r,
              savedNth: parseInt(r.nth, 10) || r.savedNth,
              last_changed_at: "just now",
            }))
          );
          setLastChangedBy((res.data.data && res.data.data.last_changed_by) || null);
          toast.success("Share it Forward settings saved.", { position: "top-center" });
        } else if (res && res.data && res.data.requires_confirmation) {
          // defensive fallback in case client-side state was stale
          toast.error("Please confirm lowering the interval and try again.", {
            position: "top-center",
          });
        } else {
          toast.error("Couldn't save your changes. Please try again.", {
            position: "top-center",
          });
        }
        dispatch(setLoading(false));
      })
      .catch((err) => {
        console.log(err);
        toast.error("Couldn't save your changes. Please try again.", {
          position: "top-center",
        });
        dispatch(setLoading(false));
      });
  };

  if (!loaded) return null;

  return (
    <div className="pay-it-forward-page">
      <div className="pif-crumb">
        {crumbLabel} <span className="sep">/</span>{" "}
        <span className="current">Share it Forward</span>
      </div>

      <div className="pif-page-head">
        <div className="pif-page-head-left">
          <div className="pif-icon-orb">
            <svg viewBox="0 0 24 24" fill="none" stroke="#500ADE" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 21s-7.5-4.6-10-9.2C.5 8.4 2.4 4.5 6 4c2.2-.3 3.9.8 6 3 2.1-2.2 3.8-3.3 6-3 3.6.5 5.5 4.4 4 7.8-2.5 4.6-10 9.2-10 9.2Z" />
            </svg>
          </div>
          <div>
            <h1>
              Share it Forward <span className="pif-badge-optional">Optional</span>
            </h1>
            <p>
              Share a portion of your client payments to support VerySoul&rsquo;s
              mission and keep evidential mediumship accessible to more people.
            </p>
            <div className="pif-info-line">You can change this anytime.</div>
            {lastChangedBy && (
              <div className="pif-last-changed">
                Last changed by <strong>{lastChangedBy.name}</strong>
                {" "}({lastChangedBy.role === "admin" ? "Admin" : "Medium"})
                {lastChangedBy.changed_at
                  ? ` on ${moment(lastChangedBy.changed_at).format("MMM D, YYYY h:mm A")}`
                  : ""}
              </div>
            )}
          </div>
        </div>
        <div className="pif-master-toggle-wrap">
          <div className="pif-master-toggle-label">{masterOn ? "On" : "Off"}</div>
          <div
            className={`pif-switch ${masterOn ? "on" : ""}`}
            onClick={handleMasterToggle}
          >
            <div className="pif-knob" />
          </div>
        </div>
      </div>

      {!masterOn && (
        <div className="pif-card pif-off-card">
          <div className="pif-off-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="#500ADE" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 21s-7.5-4.6-10-9.2C.5 8.4 2.4 4.5 6 4c2.2-.3 3.9.8 6 3 2.1-2.2 3.8-3.3 6-3 3.6.5 5.5 4.4 4 7.8-2.5 4.6-10 9.2-10 9.2Z" />
            </svg>
          </div>
          <h3>Share it Forward is off</h3>
          <p>
            Turning this on shares a portion of your client payments with VerySoul
            on a schedule you control. We&rsquo;ll start you off with sensible
            default rules for each appointment type — you can customize or turn
            off any of them right after.
          </p>
          <button className="pif-enable-cta" onClick={handleMasterToggle}>
            Turn on Share it Forward
          </button>
        </div>
      )}

      {masterOn && (
        <>
          {(() => {
            // Real data once Phase 3 ships; static placeholder until then.
            const displayImpact = impact || IMPACT_PLACEHOLDER;
            const since = displayImpact.supporting_since || enabledAt || "—";
            return (
              <div className="pif-card pif-impact-card">
                <div className="pif-impact-row">
                  <div className="pif-impact-intro">
                    <div className="pif-impact-icon-lg">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#500ADE" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 21s-7.5-4.6-10-9.2C.5 8.4 2.4 4.5 6 4c2.2-.3 3.9.8 6 3 2.1-2.2 3.8-3.3 6-3 3.6.5 5.5 4.4 4 7.8-2.5 4.6-10 9.2-10 9.2Z" />
                      </svg>
                    </div>
                    <div className="pif-impact-intro-text">
                      <h2>
                        Your Impact
                        <svg viewBox="0 0 24 24" fill="none" stroke="#500ADE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                          <path d="M12 21s-7.5-4.6-10-9.2C.5 8.4 2.4 4.5 6 4c2.2-.3 3.9.8 6 3 2.1-2.2 3.8-3.3 6-3 3.6.5 5.5 4.4 4 7.8-2.5 4.6-10 9.2-10 9.2Z" />
                        </svg>
                      </h2>
                      <p>Thank you for helping keep VerySoul accessible for people around the world.</p>
                      {/* Placeholder until Phase 3 ships a real contribution-history view */}
                      <span className="pif-impact-view-details">
                        View details
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </span>
                    </div>
                  </div>

                  <div className="pif-impact-divider" />

                  <div className="pif-impact-stat-block">
                    <div className="pif-impact-icon-sm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#500ADE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2v20M17 6.5c0-1.9-2-3-5-3s-5 1.3-5 3.3 2 2.9 5 3.4 5 1.4 5 3.5-2 3.3-5 3.3-5-1.1-5-3" />
                      </svg>
                    </div>
                    <div className="pif-stat-label">Total donated</div>
                    <div className="pif-stat-value">
                      {displayImpact.currency === "EUR" ? "€" : "$"}
                      {displayImpact.total_donated != null ? displayImpact.total_donated : 0}
                    </div>
                    <div className="pif-stat-sub">{displayImpact.currency || "USD"}</div>
                    <div className="pif-stat-sub">since {since}</div>
                  </div>

                  <div className="pif-impact-divider" />

                  <div className="pif-impact-stat-block">
                    <div className="pif-impact-icon-sm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#500ADE" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20s-6.5-4-8.5-8C2 8.5 3.5 5.5 6.5 5.5c1.7 0 2.9 1 3.5 2 .6-1 1.8-2 3.5-2 3 0 4.5 3 3 6.5-2 4-8.5 8-8.5 8Z" />
                        <path d="M7 3.5c1 .5 1.5 1.5 1.2 2.8M17 3.5c-1 .5-1.5 1.5-1.2 2.8" />
                      </svg>
                    </div>
                    <div className="pif-stat-label">Client payments shared</div>
                    <div className="pif-stat-value">{displayImpact.payments_shared_count != null ? displayImpact.payments_shared_count : 0}</div>
                    <div className="pif-stat-sub">since {since}</div>
                  </div>

                  <div className="pif-impact-divider" />

                  <div className="pif-impact-stat-block">
                    <div className="pif-impact-icon-sm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#500ADE" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3.5" y="4.5" width="17" height="16" rx="2.5" />
                        <path d="M3.5 9.5h17M8 3v3M16 3v3" />
                      </svg>
                    </div>
                    <div className="pif-stat-label">Supporting VerySoul since</div>
                    <div className="pif-stat-value pif-stat-value-sm">{since}</div>
                  </div>

                  <div className="pif-impact-note">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#500ADE" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                      <path d="M5 21c0-7 4-13 14-15-1 8-6 12-14 15Z" />
                      <path d="M5 21c2-4 4-6.5 8-9" />
                    </svg>
                    <p>
                      Every dollar you share goes directly toward the VerySoul mission—supporting
                      platform operations, free development opportunities for mediums, and making
                      evidential mediumship more accessible worldwide.
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}

          {rules.length === 0 ? (
            <div className="pif-card pif-off-card">
              <div className="pif-off-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#500ADE" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 21s-7.5-4.6-10-9.2C.5 8.4 2.4 4.5 6 4c2.2-.3 3.9.8 6 3 2.1-2.2 3.8-3.3 6-3 3.6.5 5.5 4.4 4 7.8-2.5 4.6-10 9.2-10 9.2Z" />
                </svg>
              </div>
              <h3>No session types to configure yet</h3>
              <p>
                Once you&rsquo;re approved for an appointment type — 1:1 Sessions, Coaching,
                or Group Coaching — you&rsquo;ll be able to set up sharing rules for it here.
              </p>
            </div>
          ) : (
          <div className="pif-card">
            <div className="pif-rules-head">
              <div className="pif-sub-row">
                <h2>Set how often you&rsquo;d like to share a payment for each type of appointment</h2>
                <div className="pif-calc-note">We&rsquo;ll calculate the approximate percentage for you.</div>
              </div>
              <p>Every appointment type is configured on its own schedule — there&rsquo;s no single rule that applies to everything.</p>
              {!hasPaymentIntegration && (
                <div className="pif-lock-banner">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#500ADE" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                    <rect x="4.5" y="10" width="15" height="10" rx="2" />
                    <path d="M7.5 10V7a4.5 4.5 0 0 1 9 0v3" />
                  </svg>
                  <p>
                    This medium hasn&rsquo;t connected a personal payment method yet, so
                    every payment is shared in full (100%) for now. Once a Stripe,
                    PayPal, or Square integration is added, custom sharing amounts
                    unlock here.
                  </p>
                </div>
              )}
              {hasPaymentIntegration && (
                <div className="pif-quick-actions">
                  <button type="button" className="pif-quick-all-btn" onClick={handleShareAllFull}>
                    Share All
                  </button>
                </div>
              )}
            </div>

            <div className="pif-rules-list">
              {rules.map((row) => {
                const meta = TYPE_META[row.session_type_key] || {};
                const n = parseInt(row.nth, 10) || 0;
                return (
                  <div
                    key={row.session_type_key}
                    className={`pif-rule-row ${!row.enabled ? "pif-row-disabled" : ""} ${!hasPaymentIntegration ? "pif-row-locked" : ""}`}
                  >
                    <div className="pif-rule-enable">
                      <div className="pif-re-label">Include</div>
                      <div
                        className={`pif-mini-switch ${row.enabled ? "on" : ""} ${!hasPaymentIntegration ? "pif-mini-switch-locked" : ""}`}
                        onClick={() => handleRowEnabledToggle(row.session_type_key)}
                      >
                        <div className="pif-mk" />
                      </div>
                    </div>

                    <div className="pif-rule-type">
                      <div className="pif-t-icon">{meta.icon}</div>
                      <div>
                        <div className="pif-t-title">{meta.title || row.session_type_key}</div>
                        <div className="pif-t-sub">{meta.sub}</div>
                      </div>
                    </div>

                    <div className="pif-rule-interval">
                      <div className="pif-ri-label">Share one payment every&hellip;</div>
                      <div className="pif-nth-input-wrap">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={row.nth}
                          disabled={!hasPaymentIntegration}
                          onChange={(e) => handleNthInput(row.session_type_key, e.target.value)}
                        />
                        <span className="pif-unit">{meta.unit ? meta.unit(n) : "appointments"}</span>
                        <div className="pif-stepper">
                          <button
                            type="button"
                            disabled={!hasPaymentIntegration}
                            onClick={() => handleStep(row.session_type_key, 1)}
                          >
                            &#9650;
                          </button>
                          <button
                            type="button"
                            disabled={!hasPaymentIntegration}
                            onClick={() => handleStep(row.session_type_key, -1)}
                          >
                            &#9660;
                          </button>
                        </div>
                      </div>
                      {row.last_changed_at && (
                        <div className="pif-history-mini">Last changed {row.last_changed_at}</div>
                      )}
                    </div>

                    <div className="pif-rule-percent">
                      <span className="pif-percent-pill">
                        {n <= 1 ? (
                          <>
                            100%<span className="pif-pp-note">(Every payment)</span>
                          </>
                        ) : (
                          <>&asymp;{pct(n)}%</>
                        )}
                      </span>
                    </div>

                    <div className="pif-rule-cycle">
                      <CycleIndicator nth={n} count={row.appointment_count} />
                      <div className="pif-cycle-copy">
                        {n <= 1 ? (
                          <>
                            <div className="pif-cy-head">All payments shared</div>
                            <div className="pif-cy-sub">
                              Every {(meta.title || "").toLowerCase()} session is shared
                            </div>
                          </>
                        ) : (
                          (() => {
                            // "Away" = how many MORE bookings, beyond the very
                            // next one, until a booking lands on the Nth slot
                            // (matches PayForwardEligibilityService.peek()/
                            // commit()'s (count+1) % nth === 0 trigger). E.g.
                            // nth=2: count 0 -> 1 away, 1 -> 0 away (the very
                            // next booking is free), 2 -> 1 away again. The
                            // previous formula was off by one (it measured
                            // distance back to the last completed cycle
                            // instead of forward to the next free slot).
                            const posInCycle = row.appointment_count % n;
                            const remainder = n - 1 - posInCycle;
                            return (
                              <>
                                <div className="pif-cy-head">
                                  {remainder} booking{remainder === 1 ? "" : "s"} away
                                </div>
                                <div className="pif-cy-sub">
                                  Next shared payment in {remainder} booking{remainder === 1 ? "" : "s"}
                                </div>
                              </>
                            );
                          })()
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="pif-rules-footer">
              <div className="pif-rf-item">
                These rules apply only to paid appointments. Free sessions are never included.
              </div>
              <div className="pif-rf-item">You can change your sharing rules anytime.</div>
            </div>
          </div>
          )}

          {rules.length > 0 && hasPaymentIntegration && (
            <div className="pif-page-actions">
              <button className="pif-save-cta" onClick={handleSaveClick}>
                Save changes
              </button>
            </div>
          )}
        </>
      )}

      {/* confirmation modal — shown when lowering a type's interval below its saved value */}
      <div
        className="modal fade"
        id="payItForwardConfirmModal"
        role="dialog"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-preview">
          <div className="modal-content pif-modal-content">
            <div className="modal-body">
              <h3>Are you sure you want to change this setting?</h3>
              {pendingConfirm && (
                <p>
                  For <b>{pendingConfirm.label}</b>, you&rsquo;re lowering the
                  interval from every <b>{pendingConfirm.oldNth}</b> to every{" "}
                  <b>{pendingConfirm.newNth}</b> appointments. Share it Forward will
                  apply more often — roughly <b>{pct(pendingConfirm.newNth)}%</b> of
                  these appointments instead of <b>{pct(pendingConfirm.oldNth)}%</b>.
                </p>
              )}
              <div className="pif-modal-actions">
                <button className="pif-modal-btn" onClick={handleConfirmCancel}>
                  Cancel
                </button>
                <button className="pif-modal-btn primary" onClick={handleConfirmProceed}>
                  Yes, change it
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
